import React, { useEffect, useState } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";

import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Table, Tooltip, Avatar } from "antd";
import { toast } from "react-toastify";
import {
  ChantButton,
  DescriptionText,
  IconContainer,
  ScreenHeader,
} from "../../../CommonComponents/index.js";
import useCurrency from "../../../utils/hooks/useCurrency.js";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm.jsx";
import { getDayjsFromTimestamp, isMobile } from "../../../utils/helper.jsx";
import logger from "../../../utils/logger.js";
import {
  deleteParentGiveaway,
  fetchParentGiveaways,
} from "../services/giveaways.js";

const ParentAdminGiveaways = () => {
  const [allGiveaways, setAllGiveaways] = useState([]);
  const [loading, setLoading] = useState(false);

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const currency = useCurrency();

  const navigate = useNavigate();

  const handleViewPollDetails = (data) => {
    navigate(`/clubadmin/giveaways/${data?.id}`);
  };

  const columns = [
    {
      title: "Actions",
      width: 100,
      align: "center",
      render: (data) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {data && data.id && (
              <i
                className="ml-3"
                style={{ cursor: "pointer" }}
                onClick={() => handleViewPollDetails(data)}
              >
                <EyeOutlined />
              </i>
            )}

            {data && data.id && (
              <i
                className="ml-3"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`/clubadmin/giveaways/edit/${data?.id}`)
                }
              >
                <EditOutlined />
              </i>
            )}

            {data && data.id && (
              <>
                {data?.entrantsCount === 0 ? (
                  <ChantPopConfirm
                    title="Are you sure you want to delete this giveaway?"
                    onConfirm={() => handleDeleteGiveaway(data?.id)}
                  >
                    <i
                      className="ml-3"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <DeleteOutlined />
                    </i>
                  </ChantPopConfirm>
                ) : (
                  <i
                    className="ml-3"
                    style={{
                      cursor: "not-allowed",
                      color: "gray",
                    }}
                  >
                    <DeleteOutlined />
                  </i>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      className: "header-white",
      dataIndex: "status",
      width: 100,
    },
    {
      title: "Giveaway",
      key: "giveaway",
      className: "header-white",
      dataIndex: "giveaway",
      width: 200,
    },
    {
      title: "Winners",
      key: "winners",
      className: "header-white",
      dataIndex: "winners",
      width: 200,
      render: (data) =>
        data.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {data.map((item, index) => {
              const nameInitials = item.name
                .split(" ")
                .map((word) => word[0])
                .join("")
                .toUpperCase();
              return (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <Avatar
                    src={item.image}
                    size={20}
                    style={{ borderRadius: 50 }}
                    onError={() => {
                      return true; // This prevents the default fallback image
                    }}
                  >
                    {nameInitials}
                  </Avatar>
                  {item.name}
                </div>
              );
            })}
          </div>
        ) : null,
    },
    {
      title: "Raised/Goal",
      key: "raised",
      className: "header-white",
      width: 150,
      render: (data) => {
        if (data?.goalAmount === 0) return "";

        const formatAmount = (amount) => (amount === 0 ? "0" : (amount ?? "-"));

        return `${currency}${formatAmount(data?.receivedAmount)} / ${currency}${data?.goalAmount}`;
      },
    },
    {
      title: "# Entrants",
      key: "entrantsCount",
      className: "header-white",
      dataIndex: "entrantsCount",
      width: 100,
    },
    {
      title: (
        <Tooltip title="Number of winners to be selected" placement="topLeft">
          # Winners
        </Tooltip>
      ),
      key: "winnersCount",
      className: "header-white",
      dataIndex: "winnersCount",
      width: 100,
    },
    {
      title: "Proceeds Support",
      key: "proceedsSupport",
      className: "header-white",
      dataIndex: "proceedsSupport",
      width: 150,
      ellipsis: true,
      render: (data, record) => {
        const img = `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${groupData?.id}%2Fpartner%2F${record?.partnerId}%2Flogo?alt=media`;

        return (
          <>
            {record?.partnerId ? (
              <div className="flex-center">
                <img
                  src={img}
                  alt={data?.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                  }}
                />
              </div>
            ) : (
              <span>{data?.name}</span>
            )}
          </>
        );
      },
    },
    {
      title: "Donated By",
      key: "sponsor",
      className: "header-white",
      dataIndex: "sponsor",
      width: 150,
      render: (data) => {
        return data?.image ? (
          <div className="flex-center">
            <img
              src={data?.image}
              alt="sponsor"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
              }}
            />
          </div>
        ) : (
          <span>{data?.name}</span>
        );
      },
    },
    {
      title: "Post Date",
      key: "postDate",
      className: "header-white",
      dataIndex: "postDate",
      width: 150,
      render: (data) =>
        getDayjsFromTimestamp(data)
          ? getDayjsFromTimestamp(data).format("Do MMM, YYYY")
          : "",
    },
    {
      title: "Close Date",
      key: "closedDate",
      className: "header-white",
      dataIndex: "closedDate",
      width: 150,
      render: (data) =>
        getDayjsFromTimestamp(data)
          ? getDayjsFromTimestamp(data).format("Do MMM, YYYY")
          : "",
    },
  ];

  const fetchGiveaways = async () => {
    try {
      setLoading(true);

      const { data, success, message } = await fetchParentGiveaways(
        groupData?.id,
      );
      if (success) {
        setAllGiveaways(data);
      } else {
        logger.error("Error fetching giveaways:", message);
      }
    } catch (error) {
      logger.error("Error fetching giveaways:", error);
      setAllGiveaways([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGiveaway = async (id) => {
    try {
      const { success, message } = await deleteParentGiveaway(
        groupData?.id,
        id,
      );
      if (success) {
        toast.success(message);
        fetchGiveaways();
      } else {
        toast.error(message);
      }
    } catch (error) {
      toast.error("Failed to delete giveaway");
    }
  };

  useEffect(() => {
    if (groupData?.id) {
      fetchGiveaways();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  return (
    <div style={{ padding: "12px" }}>
      <ScreenHeader
        title="Giveaways"
        elementCount={allGiveaways?.length}
        actions={
          <>
            {isMobile() ? (
              <IconContainer
                iconComponent={<PlusOutlined />}
                onClick={() => navigate("/clubadmin/giveaways/create")}
                iconStyle={{ fontSize: "16px" }}
                disabled={loading}
              />
            ) : (
              <ChantButton
                onClick={() => navigate("/clubadmin/giveaways/create")}
              >
                Create Giveaway
              </ChantButton>
            )}
          </>
        }
      />
      <div className="admin-poll-description">
        <DescriptionText>
          Create and publish giveaways here or in the app. Manage giveaway flow
          from the app (10 min warning, close, select/reselect/publish winners).
          Groups with Stripe can sell &apos;additional entries&apos; (local laws
          permitting). Feature &apos;Donated by&apos; and &apos;Proceeds
          support&apos; partners.
        </DescriptionText>
      </div>
      <Table
        bordered
        columns={columns}
        loading={loading}
        dataSource={allGiveaways}
        pagination={{
          position: ["bottomLeft"],
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        rowKey="id"
        key="id"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default ParentAdminGiveaways;
